import React, { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Seo from '../components/Seo';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, UserCircle, ArrowRight } from 'lucide-react';
import { cleanPhone, validateName, validateEmail, validateWhatsapp } from '../lib/validators';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SignupEmailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const planId = params.get('plan') || '';

  const [form, setForm] = useState({ name: '', email: '', whatsapp: '+55' });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const errors = useMemo(() => ({
    name: validateName(form.name),
    email: validateEmail(form.email),
    whatsapp: validateWhatsapp(form.whatsapp),
  }), [form]);

  const isValid = !errors.name && !errors.email && !errors.whatsapp && acceptTerms;
  const markTouched = (f) => setTouched((t) => ({ ...t, [f]: true }));
  const showError = (f) => touched[f] && errors[f];

  if (!planId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <h2 className="text-2xl font-bold mb-2">Plano não selecionado</h2>
            <p className="text-muted-foreground mb-6">Volte à página inicial e escolha um plano.</p>
            <Button onClick={() => navigate('/')}>Voltar</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const extractErrorMessage = async (res, fallback) => {
    try {
      const data = await res.json();
      if (data && typeof data.detail === 'string') return data.detail;
      if (Array.isArray(data?.detail) && data.detail[0]?.msg) return data.detail[0].msg;
      if (typeof data?.message === 'string') return data.message;
    } catch (_) {
      // body not JSON
    }
    return fallback;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, whatsapp: true });
    if (!isValid) {
      toast.error('Corrija os campos destacados antes de continuar.');
      return;
    }
    const trimmedEmail = form.email.trim().toLowerCase();
    const trimmedName = form.name.trim();
    const cleanedPhone = cleanPhone(form.whatsapp);

    setSubmitting(true);
    let res;
    try {
      res = await fetch(`${API_URL}/api/signup/request-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmedEmail,
          name: trimmedName,
          whatsapp: cleanedPhone,
          plan_id: planId,
          accept_terms: acceptTerms,
        }),
      });
    } catch (error) {
      toast.error('Sem conexão com o servidor. Verifique sua internet e tente novamente.');
      setSubmitting(false);
      return;
    }

    if (!res.ok) {
      const msg = await extractErrorMessage(res, 'Não foi possível enviar o código.');
      if (res.status === 429) {
        toast.error(msg || 'Aguarde 1 minuto antes de solicitar outro código.');
      } else {
        toast.error(msg);
      }
      setSubmitting(false);
      return;
    }

    let data;
    try {
      data = await res.json();
    } catch (_) {
      toast.error('Resposta inválida do servidor. Tente novamente.');
      setSubmitting(false);
      return;
    }

    if (data?.success) {
      toast.success('Código enviado! Verifique seu email.');
      navigate(`/signup/code?email=${encodeURIComponent(trimmedEmail)}&plan=${encodeURIComponent(planId)}`);
    } else {
      toast.error('Não foi possível enviar o código. Tente novamente.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Seo
        title="Cadastro | Watizap"
        description="Informe seus dados para criar sua conta no Watizap e enviar mensagens em massa pelo WhatsApp."
        path="/signup/email"
      />
      <Card className="max-w-md w-full">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-teal-100">
              <UserCircle className="w-5 h-5 text-teal-600" />
            </div>
            <div className="flex-1">
              <CardTitle>Seus dados</CardTitle>
              <CardDescription>
                Plano selecionado: <strong>{planId}</strong>
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <p className="text-sm text-muted-foreground">
              Vamos confirmar seu email com um código antes do pagamento, para evitar erros de digitação e garantir o acesso à sua conta.
            </p>

            {/* Name */}
            <div className="space-y-1.5">
              <Label htmlFor="name">Nome completo</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                onBlur={() => markTouched('name')}
                placeholder="Ex.: Maria da Silva"
                autoFocus
                aria-invalid={!!showError('name')}
                className={showError('name') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                data-testid="signup-name-input"
              />
              {showError('name') && (
                <p className="text-xs text-red-600" data-testid="signup-name-error">{errors.name}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <Label htmlFor="email">Seu email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                onBlur={() => markTouched('email')}
                placeholder="voce@email.com"
                aria-invalid={!!showError('email')}
                className={showError('email') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                data-testid="signup-email-input"
              />
              {showError('email') && (
                <p className="text-xs text-red-600" data-testid="signup-email-error">{errors.email}</p>
              )}
            </div>

            {/* WhatsApp */}
            <div className="space-y-1.5">
              <Label htmlFor="whatsapp">
                WhatsApp <span className="text-xs text-muted-foreground font-normal">(formato internacional)</span>
              </Label>
              <Input
                id="whatsapp"
                type="tel"
                inputMode="tel"
                value={form.whatsapp}
                onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                onBlur={() => markTouched('whatsapp')}
                placeholder="+5511999999999"
                aria-invalid={!!showError('whatsapp')}
                className={showError('whatsapp') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                data-testid="signup-whatsapp-input"
              />
              {showError('whatsapp') ? (
                <p className="text-xs text-red-600" data-testid="signup-whatsapp-error">{errors.whatsapp}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Ex.: <strong className="font-mono font-bold text-foreground">+5511999999999</strong> (código do país + DDD + número).
                </p>
              )}
            </div>

            {/* Consent checkbox (LGPD) */}
            <label className="flex items-start gap-3 pt-2 cursor-pointer select-none" data-testid="signup-consent-wrapper">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
                data-testid="signup-consent-checkbox"
              />
              <span className="text-xs text-muted-foreground leading-relaxed">
                Li e aceito a{' '}
                <a
                  href="/politica-de-privacidade"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal-600 hover:underline font-medium"
                  data-testid="signup-link-privacy"
                >
                  Política de Privacidade
                </a>{' '}
                e os{' '}
                <a
                  href="/termos-de-uso"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal-600 hover:underline font-medium"
                  data-testid="signup-link-terms"
                >
                  Termos de Uso
                </a>
                .
              </span>
            </label>

            <Button
              type="submit"
              className="w-full"
              disabled={submitting || !isValid}
              data-testid="signup-email-submit"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ArrowRight className="w-4 h-4 mr-2" />
              )}
              Enviar código de confirmação
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => navigate('/')}
              disabled={submitting}
            >
              Cancelar
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
